import { useCallback, useMemo, useState } from 'react'

const DIRECTIONS = {
  ASC: 'asc',
  DESC: 'desc',
  NULL: null,
} as const

export type SortDirection = (typeof DIRECTIONS)[keyof typeof DIRECTIONS]
export type SortConfig = {
  column: string | null
  direction: SortDirection
}

export function useTableSort<T>(initialData: T[]) {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: null,
    direction: null,
  })

  const handleSort = useCallback((column: string) => {
    setSortConfig((currentConfig) => {
      if (currentConfig.column === column) {
        const nextDirection: SortDirection =
          currentConfig.direction === DIRECTIONS.ASC
            ? DIRECTIONS.DESC
            : currentConfig.direction === DIRECTIONS.DESC
              ? DIRECTIONS.NULL
              : DIRECTIONS.ASC

        return {
          column: nextDirection ? column : null,
          direction: nextDirection,
        }
      }

      return {
        column,
        direction: DIRECTIONS.ASC,
      }
    })
  }, [])

  const sortedData = useMemo(() => {
    if (!sortConfig.column || !sortConfig.direction) {
      return initialData
    }

    return [...initialData].sort((a, b) => {
      const aValue = a[sortConfig.column as keyof T]
      const bValue = b[sortConfig.column as keyof T]

      if (aValue < bValue) {
        return sortConfig.direction === DIRECTIONS.ASC ? -1 : 1
      }
      if (aValue > bValue) {
        return sortConfig.direction === DIRECTIONS.ASC ? 1 : -1
      }
      return 0
    })
  }, [initialData, sortConfig])

  return {
    sortedData,
    sortConfig,
    handleSort,
  }
}
